import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { IonIcon, IonSpinner } from "@ionic/react";
import { close, arrowRedoOutline, heart, heartOutline, cartOutline } from "ionicons/icons";
import { Image } from "../../../components/Image/Image";
import { CustomBottomSheet } from "../../../components/UI/CustomBottomSheet";
import useGetProductByIdQuery from "../../../providers/products/useGetProductByIdQuery";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import { useCartData } from "../../../providers/cartDataStore";
import useGetProductsQuery from "../../../providers/products/useGetProductsQuery";
import useGetCategoryByIdQuery from "../../../providers/categories/useGetCategoryByIdQuery";
import ProductDetailBottomSheetSkeleton from "./ProductDetailSheetSkeleton";
import { useProductSubscriptionsStore } from "../../../providers/products";
import { ProductShelf, QuantityInput } from "../../../parhato2/src/entities";
import { useWishlistToggle } from "../../../providers/wishlist";
import ProductDetailItem from "./ProductDetailItem";
import { useConfig } from "../../../providers/config";
import useAddProductSubscription from "../../../hooks/useAddProductSubscription";
import { useProductDetailSheetStore } from "../../../providers/productDetailSheetStore";
import { ImageType, ProductImageTypes, Product as TProduct, ProductType, Product } from "@core/types";
import useGetProductVariations from "../../../providers/products/useGetProductVariations";
import { SelectVariationAttribute } from "../../../fsd/features/SelectVatiationAttribute/SelectVatiationAttribute";
import { useTranslation } from "react-i18next";
import { useProductCardActions } from "../../../providers/useProductActions";
import { Share } from "@capacitor/share";
import cln from "classnames/bind";
import { Clipboard } from "@capacitor/clipboard";
import { useIsLg } from "../../../hooks/useIsLg";
import { BackButtonEvent } from "@ionic/core/components";
import { Capacitor } from "@capacitor/core";
import styles from "./ProductDetailBottomSheet.module.scss";

const cx = cln.bind(styles);

const ProductDetailBottomSheet = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const closeStore = useProductDetailSheetStore((s) => s.close);
  const id = useProductDetailSheetStore((s) => s.productId)!;
  const type = useProductDetailSheetStore((s) => s.productType);

  const [isOpen, setIsOpen] = useState(true);
  const isLg = useIsLg();
  const { activeShippingZone, isLoading: isActiveOrMainShippingZoneLoading } = useActiveOrMainShippingZone();
  const { cartProductsMap } = useCartData();
  const { addToCart, removeProductFromCart } = useProductCardActions();
  const { toggleWish, isWishProduct } = useWishlistToggle();
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const heartButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [selectedVariation, setSelectedVariation] = useState<TProduct | null>(null);
  const singleProductEnabled = !!id && !!activeShippingZone.id;
  const {
    data: product,
    isLoading: isSingleProductLoading,
    isFetching: isSingleProductFetching,
  } = useGetProductByIdQuery(id, activeShippingZone.id, {
    options: {
      enabled: singleProductEnabled,
    },
    params: {
      type,
    },
  });

  const history = useHistory();

  const isDiscount =
    product?.discountPrice !== "" && product?.discountPrice !== null && product?.discountPrice !== product?.price;

  const variationsEnabled = !!product?.variations?.length && !!activeShippingZone.id;

  const {
    data: variations,
    isFetching: isVariationsFetching,
    isLoading: isVariationsLoading,
  } = useGetProductVariations(product?.variations ?? [], activeShippingZone.id, {
    enabled: variationsEnabled,
  });

  const handleVariationSelect = (variation: TProduct | null) => {
    setSelectedVariation(variation);
  };

  const productForCart =
    product?.type === ProductType.VARIABLE ? { ...selectedVariation, images: product.images } : product;
  const productName = product?.name;
  const productWeight = productForCart?.weight;
  const cartQuantity = productForCart?.id ? (cartProductsMap[productForCart?.id]?.quantity ?? 0) : 0;
  const productImage = {
    ...(productForCart?.images?.[0] || product?.images?.[0]),
    type: ProductImageTypes.img,
  } as ImageType;

  const { productSubscriptionIdsMap } = useProductSubscriptionsStore.getState();
  const productSubscription = productSubscriptionIdsMap[product?.id!];

  const { handleAddProductSubscription, isLoading: isAddProductSubscriptionLoading } = useAddProductSubscription(
    product?.id!,
  );

  const { data: category } = useGetCategoryByIdQuery(product?.categories?.[0]?.id ?? "", {
    enabled: !!product?.categories?.length,
  });

  const categoryProductsEnabled = !!category?.id && !!activeShippingZone.id;

  const { data: categoryProductsResults, isLoading: isCategoryProductsLoading } = useGetProductsQuery(
    {
      zoneId: activeShippingZone.id,
      categoryId: category?.id,
    },
    {
      enabled: categoryProductsEnabled,
    },
  );

  const categoryProducts = categoryProductsResults?.products;
  const isVariable = product?.type === ProductType.VARIABLE;
  const isDisabled = isVariable && !selectedVariation;
  const handleClose = (e?: React.MouseEvent<Element>) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setIsOpen(false);

    setTimeout(() => closeStore(), 500);
  };

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handler = (event: BackButtonEvent) => {
      event.detail.register(101, () => {
        handleClose();
      });
    };

    document.addEventListener("ionBackButton", handler as EventListener);
    return () => {
      document.removeEventListener("ionBackButton", handler as EventListener);
    };
  }, []);

  const handleRemoveProductFromCart = (id: string) => {
    removeProductFromCart(id);
  };

  const handleShareProduct = async () => {
    const url = `${config?.appUrl}/product-detail-sheet/${id}`;

    const text = `Вот что есть в Parhato: ${url}`;

    await Share.share({
      title: productName,
      text,
      dialogTitle: t("Share"),
    });
  };

  const roundedCartQuantity = cartQuantity.toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    compactDisplay: "short",
    maximumFractionDigits: 2,
  });

  const isNutritionalInfoEmpty = !!(
    product?.energyValue?.length ||
    product?.proteins?.length ||
    product?.fats?.length ||
    product?.percentFat?.length ||
    product?.carbohydrates?.length
  );

  const isLoading =
    ((isSingleProductLoading || isSingleProductFetching) && singleProductEnabled) ||
    ((isVariationsLoading || isVariationsFetching) && variationsEnabled) ||
    isActiveOrMainShippingZoneLoading;

  const handleCopy = async () => {
    await Clipboard.write({
      string: productName,
    });
  };

  const handleGoToCart = () => {
    setIsOpen(false);
    setTimeout(() => {
      closeStore();
      history.push("/cart");
    }, 100);
  };

  const handleGoToProductCategory = () => {
    setIsOpen(false);
    setTimeout(() => {
      closeStore();
      history.push(`/category-detail/${category?.id}`);
    }, 100);
  };

  return (
    <CustomBottomSheet
      open={isOpen}
      onDismiss={handleClose}
      showDefaultHeader={false}
      showDefaultPreview={false}
      contentClassName={cx("productDetailBottomSheet", "z-[99999]")}
      snapPoints={({ maxHeight }) => (isLg ? [maxHeight * 0.8, maxHeight] : [maxHeight, maxHeight])}
    >
      {isLoading ? (
        <ProductDetailBottomSheetSkeleton />
      ) : (
        <div className="lg:p-2.5">
          <div className="flex flex-col justify-center gap-4 ">
            <div className="w-full rounded-md flex flex-col items-end px-2">
              <div className="flex flex-row items-center gap-2">
                {!config?.shareProduct && (
                  <button
                    ref={shareButtonRef}
                    onClick={handleShareProduct}
                    className="flex items-center justify-end p-2 cursor-pointer rounded-full bg-gray-100 outline-none"
                  >
                    <IonIcon icon={arrowRedoOutline} size="small" />
                  </button>
                )}
                <button
                  ref={heartButtonRef}
                  onClick={() => toggleWish(product?.id!)}
                  className="flex items-center justify-end p-2 cursor-pointer rounded-full bg-gray-100 outline-none"
                >
                  <IonIcon size="small" icon={isWishProduct(product?.id!) ? heart : heartOutline} />
                </button>
                <button
                  ref={closeButtonRef}
                  onClick={(e) => handleClose(e)}
                  className="flex items-center justify-end p-2 cursor-pointer rounded-full bg-gray-100 outline-none"
                >
                  <IonIcon icon={close} size="small" />
                </button>
              </div>
              <Image
                image={productImage}
                placeholder={productImage.base64}
                className="w-full h-64 object-contain"
                lazyLoad={false}
              />
            </div>
            <div className="flex flex-col items-start justify-start gap-2 px-4">
              <div className="flex flex-col justify-between gap-3 w-full pb-3 border-b border-[#f2f2f2]">
                <div className="flex flex-col items-start gap-4 justify-center text-lg leading-5">
                  <div className="w-full flex items-center justify-between gap-2">
                    <div>
                      <div onClick={handleCopy} className="inline font-bold">
                        {productName}
                      </div>
                      {Number(productWeight) > 0 && (
                        <div className="inline ml-2 text-[#A6A6A6] text-sm">
                          {Number(productWeight)} {t("KGM")}
                        </div>
                      )}
                    </div>
                  </div>
                  {isVariationsFetching && <IonSpinner />}
                  {variations && product?.type === ProductType.VARIABLE && !isVariationsFetching && (
                    <SelectVariationAttribute
                      variations={variations as TProduct[]}
                      onVariationSelect={handleVariationSelect}
                    />
                  )}
                  {product?.description && <div className="text-sm">{product?.description}</div>}
                </div>
              </div>

              {/* Nutritional information */}
              {isNutritionalInfoEmpty && (
                <div className="w-full flex items-center justify-between gap-1 pb-2 border-b border-[#f2f2f2]">
                  {product?.energyValue && (
                    <ProductDetailItem label={t("Energy")} value={product?.energyValue} unit="ккал" />
                  )}
                  {product?.proteins && <ProductDetailItem label={t("Proteins")} value={product?.proteins} unit="г" />}
                  {product?.fats && <ProductDetailItem label={t("Fats")} value={product?.fats} unit="г" />}
                  {product?.percentFat && <ProductDetailItem label={t("Fat %")} value={product?.percentFat} unit="%" />}
                  {product?.carbohydrates && (
                    <ProductDetailItem label={t("Carbs")} value={product?.carbohydrates} unit="г" />
                  )}
                </div>
              )}

              {product?.sku && <ProductDetailItem label={t("Article")} value={product?.sku} canCopyValue />}

              {product?.composition && <ProductDetailItem label={t("Composition")} value={product?.composition} />}

              {/* Brand and storage conditions */}
              <div className="flex flex-col gap-1">
                {product?.brand && <ProductDetailItem label={t("Brand")} value={product?.brand} />}
                {product?.storageConditions && (
                  <ProductDetailItem label={t("Storage")} value={product?.storageConditions} />
                )}
              </div>

              {/* Add to cart */}
              <div className="flex w-full my-3">
                {productForCart?.stockQuantity! > 0 ? (
                  cartQuantity > 0 ? (
                    <div className="flex flex-col w-full gap-2.5">
                      <div className="flex items-center h-[3.125rem] justify-between text-base w-full border py-2.5 border-primary rounded-lg bg-primary ">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveProductFromCart(productForCart?.id!);
                          }}
                          className={`flex flex-1 items-center justify-center p-0 m-0 ${isDisabled ? "text-muted-foreground" : "text-primary-foreground"}`}
                        >
                          <span className="font-bold text-base leading-none">−</span>
                        </button>
                        <div
                          className={cx("font-bold flex items-center gap-1.5", isVariable && "flex-nowrap shrink-0")}
                        >
                          {config.isCartQuantityInputEnabled ? (
                            <span className="inline-flex shrink-0 items-center justify-center bg-white text-black rounded-lg px-2.5 py-1.5">
                              <QuantityInput
                                autoFit
                                product={productForCart as Product}
                                cartQuantity={cartQuantity}
                                className="appearance-none bg-transparent outline-none border-0 p-0 m-0 text-center font-bold leading-none"
                              />
                            </span>
                          ) : (
                            <span className="inline-flex shrink-0 items-center justify-center bg-white text-black rounded-lg px-2.5 py-1.5 min-w-[1.75ch]">
                              {roundedCartQuantity}
                            </span>
                          )}
                          <span>×</span>

                          {isDiscount && <span className="text-nowrap">{productForCart?.discountPrice} ₽</span>}
                          <span
                            className={cx(
                              "font-bold",
                              isVariable ? "text-nowrap whitespace-nowrap" : "w-12 text-center",
                              isDiscount && "text-nowrap line-through text-xs opacity-70",
                            )}
                          >
                            {productForCart?.price} ₽
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(productForCart as Product);
                          }}
                          disabled={cartQuantity >= productForCart?.stockQuantity!}
                          className={`flex flex-1 items-center justify-center p-0 m-0 ${isDisabled ? "text-muted-foreground" : "text-primary-foreground"}`}
                        >
                          <span className="font-bold text-base leading-none">+</span>
                        </button>
                      </div>

                      <button
                        onClick={handleGoToCart}
                        className="flex h-[3.125rem] w-full rounded-lg bg-primary items-center justify-center"
                      >
                        <div className="flex items-center text-center gap-1.5">
                          {t("Go to cart")}
                          <IonIcon icon={cartOutline} />
                        </div>
                      </button>
                    </div>
                  ) : (
                    <button
                      className="bg-primary w-full py-2.5 rounded-lg h-[3.125rem]"
                      onClick={() => addToCart(productForCart as Product)}
                      disabled={isDisabled}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span className={cx("font-semibold h-full text-md", isDiscount && "line-through")}>
                          {productForCart?.price} ₽
                        </span>
                        {isDiscount && (
                          <span className="font-semibold h-full text-lg">{productForCart?.discountPrice} ₽</span>
                        )}
                        <span className="font-semibold h-full text-lg">+</span>
                      </div>
                    </button>
                  )
                ) : (
                  <button
                    disabled={!!productSubscription || isAddProductSubscriptionLoading}
                    onClick={handleAddProductSubscription}
                    className={`w-full text-xs rounded-lg py-2.5 font-semibold text-white bg-[#CDCDCD] ${
                      !!productSubscription || isAddProductSubscriptionLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {productSubscription ? t("Coming soon") : t("Notify")}
                  </button>
                )}
              </div>
            </div>
          </div>

          <ProductShelf
            onCategoryClick={handleGoToProductCategory}
            showCategoryButton={!!category?.id}
            className="my-2.5 mx-2.5"
            classnameSkeleton="!my-2.5"
            items={categoryProducts!}
            categoryName={category?.name}
            isLoading={categoryProductsEnabled && isCategoryProductsLoading}
          />
        </div>
      )}
    </CustomBottomSheet>
  );
};

export default ProductDetailBottomSheet;
